import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useId, useState } from "react";
import { ApiError } from "../lib/api";
import {
  createTrack,
  deleteTrack,
  fetchManagedTracks,
  LIMITS,
  MAX_TRACKS,
  type ManagedTrack,
  queryKeys,
  reorderTracks,
  TRACK_PALETTE,
  updateTrack,
} from "./api";

/**
 * Reglages > Pistes (#588 ; spec, section 4) : le proprietaire ajoute, renomme, recolore,
 * reordonne, masque et supprime ses pistes. Une piste pour lui seul, comme « Moi » ou « Santé » :
 * naissance, permis, hospitalisations. Tout est au clavier (boutons Monter et Descendre, pas de
 * glisser-deposer), et la couleur ne porte jamais seule le sens : le nom est toujours ecrit.
 *
 * Une piste masquee disparait de la frise, des choix de saisie, des proches et des liens ; ses
 * points restent et reviennent avec elle. Une piste qui porte un point ou une proposition ne se
 * supprime pas : le serveur le refuse (409), et le bouton le dit avant meme le clic.
 */

/** Le nom de chaque couleur de la palette, pour les lecteurs d'ecran et les infobulles. */
export const COLOR_NAMES: Record<(typeof TRACK_PALETTE)[number], string> = {
  "#FF8A65": "Corail",
  "#7FA8FF": "Bleu",
  "#6FD3A0": "Menthe",
  "#B79CFF": "Lilas",
  "#5ED3E6": "Turquoise",
  "#FF7EB6": "Rose",
  "#F2C14E": "Or",
  "#A8D672": "Vert pomme",
  "#E57373": "Rouge doux",
  "#4DB6AC": "Sarcelle",
  "#9FA8DA": "Pervenche",
  "#C5A880": "Sable",
};

const colorName = (color: string): string =>
  (COLOR_NAMES as Record<string, string>)[color] ?? color;

const BUTTON = "min-h-11 rounded-[10px] border border-input px-3 text-[13px] disabled:opacity-60";

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.message : "La demande n'a pas abouti. Réessaie.";

/** La palette : une rangee de pastilles-boutons, la couleur choisie marquee `aria-pressed`. */
function ColorPicker({
  label,
  value,
  onPick,
  disabled,
}: {
  label: string;
  value: string;
  onPick: (color: string) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
      <legend className="sr-only">{label}</legend>
      {TRACK_PALETTE.map((color) => (
        <button
          aria-label={colorName(color)}
          aria-pressed={value === color}
          className={`inline-flex size-11 items-center justify-center rounded-full border-2 bg-transparent ${value === color ? "border-foreground" : "border-transparent"}`}
          disabled={disabled === true}
          key={color}
          onClick={() => onPick(color)}
          title={colorName(color)}
          type="button"
        >
          <span
            aria-hidden="true"
            className="size-6 rounded-full"
            style={{ background: color, boxShadow: `0 0 8px ${color}` }}
          />
        </button>
      ))}
    </fieldset>
  );
}

export function TracksSettings() {
  const queryClient = useQueryClient();
  const headingId = useId();
  const tracks = useQuery({
    queryKey: queryKeys.managedTracks,
    queryFn: fetchManagedTracks,
    retry: false,
  });
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.managedTracks });
    // La frise et les choix de saisie suivent : nom, couleur, ordre, masquage.
    void queryClient.invalidateQueries({ queryKey: queryKeys.frise });
  };
  const run = useMutation({
    mutationFn: (work: () => Promise<unknown>) => work(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (thrown) => setError(messageOf(thrown)),
  });

  const list = tracks.data?.tracks ?? [];

  const move = (index: number, delta: -1 | 1) => {
    const ids = list.map((track) => track.id);
    const target = index + delta;
    const [moved] = ids.splice(index, 1);
    if (moved === undefined || target < 0 || target > ids.length) return;
    ids.splice(target, 0, moved);
    run.mutate(() => reorderTracks(ids));
  };

  return (
    <section aria-labelledby={headingId} className="flex max-w-md flex-col gap-2.5">
      <h2 className="m-0 text-[15px] font-semibold" id={headingId}>
        Pistes
      </h2>
      <p className="m-0 text-xs text-muted-foreground">
        Une piste pour toi seul, comme Moi ou Santé : naissance, permis, hospitalisations. Masquée,
        une piste disparaît de ta frise, de celle de tes proches et des liens ; ses moments
        reviennent quand tu l'affiches.
      </p>
      {tracks.isPending ? (
        <p className="m-0 text-sm text-muted-foreground" role="status">
          Chargement...
        </p>
      ) : null}
      {tracks.isError ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          Tes pistes ne répondent pas pour le moment.
        </p>
      ) : null}
      {error !== null ? (
        <p className="m-0 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {list.map((track, index) => (
          <TrackRow
            busy={run.isPending}
            first={index === 0}
            key={track.id}
            last={index === list.length - 1}
            onDelete={() => run.mutate(() => deleteTrack(track.id))}
            onMove={(delta) => move(index, delta)}
            onPatch={(patch) => run.mutate(() => updateTrack(track.id, patch))}
            track={track}
          />
        ))}
      </ol>
      <AddTrack count={list.length} onAdded={refresh} />
    </section>
  );
}

function TrackRow({
  track,
  first,
  last,
  busy,
  onMove,
  onPatch,
  onDelete,
}: {
  track: ManagedTrack;
  first: boolean;
  last: boolean;
  busy: boolean;
  onMove: (delta: -1 | 1) => void;
  onPatch: (patch: { name?: string; color?: string; hidden?: boolean }) => void;
  onDelete: () => void;
}) {
  const id = useId();
  const [name, setName] = useState(track.name);
  const [picking, setPicking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setName(track.name), [track.name]);

  const used = track.entryCount + track.proposalCount;
  const commitName = () => {
    const next = name.trim();
    if (next === "") {
      setName(track.name);
      return;
    }
    if (next !== track.name) onPatch({ name: next });
  };

  return (
    <li
      className={`flex flex-col gap-2 rounded-xl border border-border bg-card p-3 ${track.hidden ? "opacity-70" : ""}`}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="size-3.5 shrink-0 rounded-full"
          style={{ background: track.color, boxShadow: `0 0 8px ${track.color}` }}
        />
        <input
          aria-label={`Nom de la piste ${track.name}`}
          className="min-h-11 min-w-0 flex-1 rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground"
          disabled={busy}
          maxLength={LIMITS.trackName}
          onBlur={commitName}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitName();
            }
          }}
          value={name}
        />
      </div>
      <p className="m-0 text-xs text-muted-foreground" id={`${id}-etat`}>
        {track.hidden ? "Masquée · " : ""}
        {used === 0
          ? "Aucun moment"
          : `${String(track.entryCount)} ${track.entryCount <= 1 ? "moment" : "moments"}${
              track.proposalCount > 0
                ? ` · ${String(track.proposalCount)} ${track.proposalCount === 1 ? "proposition" : "propositions"}`
                : ""
            }`}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          aria-label={`Monter ${track.name}`}
          className={BUTTON}
          disabled={busy || first}
          onClick={() => onMove(-1)}
          type="button"
        >
          Monter
        </button>
        <button
          aria-label={`Descendre ${track.name}`}
          className={BUTTON}
          disabled={busy || last}
          onClick={() => onMove(1)}
          type="button"
        >
          Descendre
        </button>
        <button
          aria-expanded={picking}
          aria-label={`Couleur de ${track.name}`}
          className={BUTTON}
          onClick={() => setPicking(!picking)}
          type="button"
        >
          Couleur
        </button>
        <button
          aria-label={`${track.hidden ? "Afficher" : "Masquer"} ${track.name}`}
          className={BUTTON}
          disabled={busy}
          onClick={() => onPatch({ hidden: !track.hidden })}
          type="button"
        >
          {track.hidden ? "Afficher" : "Masquer"}
        </button>
        {confirming ? (
          <span className="flex gap-2">
            <button
              className="min-h-11 rounded-lg border border-destructive px-3 text-[13px] text-destructive disabled:opacity-60"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                onDelete();
              }}
              type="button"
            >
              Oui, supprimer
            </button>
            <button className={BUTTON} onClick={() => setConfirming(false)} type="button">
              Annuler
            </button>
          </span>
        ) : (
          <button
            aria-describedby={used > 0 ? `${id}-pas-vide` : undefined}
            aria-label={`Supprimer ${track.name}`}
            className={BUTTON}
            disabled={busy || used > 0}
            onClick={() => setConfirming(true)}
            type="button"
          >
            Supprimer
          </button>
        )}
      </div>
      {used > 0 ? (
        <p className="m-0 text-xs text-muted-foreground" id={`${id}-pas-vide`}>
          Elle porte encore des moments : masque-la plutôt que de la supprimer.
        </p>
      ) : null}
      {picking ? (
        <ColorPicker
          disabled={busy}
          label={`Couleur de ${track.name}`}
          onPick={(color) => {
            setPicking(false);
            if (color !== track.color) onPatch({ color });
          }}
          value={track.color}
        />
      ) : null}
    </li>
  );
}

function AddTrack({ count, onAdded }: { count: number; onAdded: () => void }) {
  const id = useId();
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(TRACK_PALETTE[0]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => createTrack(name.trim(), color),
    onSuccess: (track) => {
      setDone(`La piste « ${track.name} » est ajoutée.`);
      setName("");
      onAdded();
    },
    onError: (thrown) => setError(messageOf(thrown)),
  });
  const full = count >= MAX_TRACKS;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setDone(null);
    if (name.trim() === "") {
      setError("Donne un nom à la piste.");
      return;
    }
    add.mutate();
  };

  return (
    <form
      aria-labelledby={`${id}-titre`}
      className="flex flex-col gap-2 border-t border-border pt-3"
      noValidate
      onSubmit={submit}
    >
      <h3 className="m-0 text-sm font-semibold" id={`${id}-titre`}>
        Ajouter une piste
      </h3>
      <label className="text-[13px] text-[#C9D0DE]" htmlFor={`${id}-nom`}>
        Nom de la piste
      </label>
      <input
        autoComplete="off"
        className="min-h-11 rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground"
        disabled={full}
        id={`${id}-nom`}
        maxLength={LIMITS.trackName}
        onChange={(event) => setName(event.target.value)}
        value={name}
      />
      <ColorPicker label="Couleur de la nouvelle piste" onPick={setColor} value={color} />
      <button
        className="min-h-11 self-start rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        disabled={add.isPending || full}
        type="submit"
      >
        Ajouter la piste
      </button>
      {full ? (
        <p className="m-0 text-xs text-muted-foreground">
          {String(MAX_TRACKS)} pistes au plus : supprime ou masque-en une avant d'en ajouter.
        </p>
      ) : null}
      <div aria-live="polite">
        {done !== null ? <p className="m-0 text-sm text-tl-ok">{done}</p> : null}
        {error !== null ? (
          <p className="m-0 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </form>
  );
}
